// Normalise a stored TipTap document so ProseMirror's schema validator
// accepts it. Four failure modes are handled:
//
//   1. `{ type: "text", text: "" }` — TipTap rejects empty text nodes with
//      "Empty text nodes are not allowed" and renders nothing.
//
//   2. Null-valued schema fields, e.g. block nodes serialised as
//      `{ type: "paragraph", text: null, attrs: null, marks: null,
//      content: null }`. ProseMirror expects `attrs` to be an object,
//      `marks` to be an array, `text` to live only on text nodes, and
//      `content` to be an array — any `null` makes the whole doc invalid
//      and the editor mounts empty.
//
//   3. Legacy `referenceMention` nodes from older flow definitions. The
//      schema only registers `mention`; references render as a regular
//      mention carrying `color: "fuchsia"` and `prefix: "@"` (see
//      chipUtils.ts:85-89). An unknown node type throws
//      "Unknown node type: referenceMention" and aborts the whole doc.
//
//   4. Trailing `hardBreak` nodes at the end of a block's inline content.
//      prosemirror-markdown 1.13.3's `isMarkAhead` double-increments its
//      cursor while scanning past hardBreaks (once inside the loop body
//      and once via the `for(;;index++)` step) and uses `==` for the
//      bounds check, so it overshoots `childCount` and throws
//      "Index N out of range" the moment `getMarkdown()` runs on a doc
//      shaped like `<bold("..."), hardBreak, hardBreak, ...>`. Stripping
//      trailing hardBreaks is semantically a no-op and avoids the crash.
//
// Walks the tree recursively so nested lists are cleaned too. Always
// returns a fresh object — input is not mutated.

type TiptapNode = {
  type?: string;
  text?: unknown;
  attrs?: unknown;
  marks?: unknown;
  content?: unknown;
  [key: string]: unknown;
};

const REFERENCE_MENTION_ATTRS = {
  mentionSuggestionChar: "@",
  prefix: "@",
  icon: "@",
  color: "fuchsia",
};

function isEmptyTextNode(node: TiptapNode): boolean {
  return node.type === "text" && (typeof node.text !== "string" || !node.text);
}

function isHardBreakNode(node: TiptapNode): boolean {
  return node.type === "hardBreak";
}

function migrateLegacyType(node: TiptapNode): TiptapNode {
  if (node.type !== "referenceMention") return node;
  const incomingAttrs =
    node.attrs && typeof node.attrs === "object" && !Array.isArray(node.attrs)
      ? (node.attrs as Record<string, unknown>)
      : {};
  return {
    ...node,
    type: "mention",
    attrs: { ...REFERENCE_MENTION_ATTRS, ...incomingAttrs },
  };
}

function sanitizeNode(node: TiptapNode): TiptapNode {
  const migrated = migrateLegacyType(node);
  const result: TiptapNode = {};
  if (typeof migrated.type === "string") result.type = migrated.type;

  if (typeof migrated.text === "string") result.text = migrated.text;

  if (
    migrated.attrs &&
    typeof migrated.attrs === "object" &&
    !Array.isArray(migrated.attrs)
  ) {
    result.attrs = migrated.attrs;
  }

  if (Array.isArray(migrated.marks) && migrated.marks.length > 0) {
    result.marks = migrated.marks;
  }

  if (Array.isArray(migrated.content)) {
    const sanitized = (migrated.content as TiptapNode[])
      .map((child) => sanitizeNode(child))
      .filter((child) => !isEmptyTextNode(child));
    while (
      sanitized.length > 0 &&
      isHardBreakNode(sanitized[sanitized.length - 1])
    ) {
      sanitized.pop();
    }
    if (sanitized.length > 0) result.content = sanitized;
  }

  return result;
}

export function sanitizeTiptapContent<
  T extends Record<string, unknown> | undefined,
>(doc: T): T {
  if (!doc || typeof doc !== "object") return doc;
  return sanitizeNode(doc as TiptapNode) as T;
}
